import * as THREE from 'three';
import {getCarcaseTextures} from 'components/customer/Preview3DCommon/shapes/advancedShelf';
import {
    applyCarcaseMaterial,
    attachWireframeToggleEvent as attachEvents,
    createLineSegments,
} from 'components/customer/Preview3DCommon/shelves/common';
import {PreviewFormValues} from 'components/customer/Preview3D/types';
import {
    generateDrawers,
    getDrawerFormValues,
} from 'components/customer/Preview3DCommon/values/drawer';
import {TemplateVariable} from 'components/customer/Preview3D/usePreview3DData';

export const renderArrowShapePanelForLSDrawers = (
    values: PreviewFormValues,
    variables: TemplateVariable
) => {
    const carcaseThickness = values.cabinet_carc_thickness;
    const exteriorThickness = values.cabinet_ext_thickness;
    const leftWidth = Number(values.cabinet_left_width);
    const rightWidth = Number(values.cabinet_right_width);
    const leftDepth = Number(values.cabinet_left_depth);
    const rightDepth = Number(values.cabinet_right_depth);

    const {carcaseTexture, carcaseEdgeTexture} = getCarcaseTextures(values);

    const data = generateDrawers(
        values.drawers,
        getDrawerFormValues(values),
        variables,
        {
            ...values,
            cabinet_carc_thickness: carcaseThickness,
            cabinet_ext_thickness: exteriorThickness,
        }
    );
    const meshes: THREE.Mesh[] = [];
    data?.drawers.forEach((drawer, index) => {
        const mesh = generateArrowShapeMesh({
            leftWidth,
            leftDepth,
            rightDepth,
            rightWidth,
            carcaseTexture,
            carcaseEdgeTexture,
            carcaseThickness,
        });

        mesh.userData.isCornerDrawerPart = true;
        mesh.userData.index = index;

        mesh.position.set(
            carcaseThickness + 25,
            leftWidth - 25,
            drawer.bottomPosition
        );

        mesh.scale.y = -1;

        const lineSegments = createLineSegments(mesh);

        mesh.add(lineSegments);

        attachEvents(mesh);
        meshes.push(mesh);
    });

    return meshes;
};

export const generateArrowShapeMesh = ({
    leftWidth,
    leftDepth,
    rightDepth,
    rightWidth,
    carcaseTexture,
    carcaseEdgeTexture,
    carcaseThickness,
    horizontalGrainCarc,
    showTexture,
}: {
    leftWidth: number;
    leftDepth: number;
    rightDepth: number;
    rightWidth: number;
    carcaseTexture: THREE.Texture;
    carcaseEdgeTexture: THREE.Texture;
    carcaseThickness: number;
    horizontalGrainCarc?: boolean;
    showTexture?: boolean;
}) => {
    // Create the shape
    const shape = new THREE.Shape();

    const leftInsideWidth = leftWidth - carcaseThickness - carcaseThickness; // based on the workbook we are cutting twice
    const rightInsideWidth =
        rightWidth - carcaseThickness - carcaseThickness - carcaseThickness;

    const leftInsideDepth = leftDepth - carcaseThickness;

    const drawerOffset = 25;
    const drawerOffset45 = drawerOffset / Math.sqrt(2);
    const bottomCutoutWidth = 32 * Math.sqrt(2);

    const leftWidthOffset = 1.125 * carcaseThickness - 9.5625;
    const rightWidthOffset = 1.75 * carcaseThickness - 20.875;

    const workableLeftWidth =
        leftInsideWidth - drawerOffset - drawerOffset45 + leftWidthOffset;
    const workableRightWidth =
        rightInsideWidth - drawerOffset - drawerOffset45 + rightWidthOffset;

    // start drawing the panel
    shape.moveTo(0, 0);
    // drawer back left
    shape.lineTo(0, leftWidth - leftDepth - drawerOffset);
    // drawer left side
    shape.lineTo(
        leftInsideDepth - drawerOffset - bottomCutoutWidth / 2,
        workableLeftWidth - bottomCutoutWidth / 2
    );
    // draw the 32mm gap
    shape.lineTo(
        leftInsideDepth - drawerOffset,
        workableLeftWidth - bottomCutoutWidth
    );

    // drawer left depth
    shape.lineTo(
        leftInsideDepth - drawerOffset,
        rightDepth - drawerOffset - carcaseThickness
    );

    // drawer right depth
    shape.lineTo(
        workableRightWidth - bottomCutoutWidth,
        rightDepth - drawerOffset - carcaseThickness
    );
    // draw gap 32 mm
    shape.lineTo(
        workableRightWidth - bottomCutoutWidth / 2,
        rightDepth - carcaseThickness - drawerOffset - drawerOffset45 - 5
    );
    // drawer right side
    shape.lineTo(rightWidth - rightDepth - drawerOffset - drawerOffset45, 0);
    // close shape
    shape.lineTo(0, 0);

    const extrudeSettings = {
        steps: 1,
        depth: carcaseThickness,
        bevelEnabled: false,
    };

    const geometry = new THREE.ExtrudeGeometry(shape, extrudeSettings);

    const materials = applyCarcaseMaterial({
        geometry,
        texture: carcaseTexture,
        edgeTexture: carcaseEdgeTexture,
        useSubstrateEdge: false,
        flipGrain: false,
        hasHorizontalGrain: horizontalGrainCarc,
        showTexture,
    });

    // Apply materials to faces
    for (let i = 0; i < geometry.groups.length; i++) {
        geometry.groups[Number(i)].materialIndex = i % 2; // Alternate materials
    }

    // Create the mesh
    return new THREE.Mesh(geometry, materials);
};
